import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CopyClipboard from "../main/CopyClipboard";
import { EyeIcon } from "@heroicons/react/24/outline";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { getMyUserToken } from "../api/DeveloperAppApi";
import useUpgradePlan from "./hooks/useUpgradePlan";
import GridTable from "../grid-v2/GridTable";
import { Key, Trash2 } from "lucide-react";

const tokenPlaceHolder = "*********************************************************";

const DeveloperSetting = () => {
  const { t } = useTranslation();
  const { invalidFeature } = useUpgradePlan();

  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>(searchText);

  const [myToken, setMyToken] = useState(tokenPlaceHolder);

  const showCopy = myToken !== tokenPlaceHolder;

  const tokenHeaders = [
    { key: "name", title: "name" },
    { key: "key", title: "key" },
    { key: "action", title: "" },
  ];

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 800);
    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  const handleClickShowToken = () => {
    if (invalidFeature("apiAccess")) return;
    getMyUserToken().then((data) => {
      setMyToken(data.value);
    });
  };

  const filterData = useMemo(() => {
    let datas = [{ id: 1, name: "API Key", key: myToken, created: new Date() }].sort(
      (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
    );
    if (debouncedSearchText) {
      datas = datas.filter(
        (item) =>
          item.name &&
          item.name.trim().toLowerCase().includes(debouncedSearchText.trim().toLowerCase())
      );
    }
    return datas.map((item) => {
      return {
        name: item.name,
        key: (
          <div className="flex items-center gap-4 max-w-2xl">
            <div className="truncate">{item.key}</div>
            <div onClick={handleClickShowToken} className="cursor-pointer">
              <EyeIcon className={"w-4"} />
            </div>
            {showCopy && <CopyClipboard copingText={myToken} />}
          </div>
        ),
        action: (
          <div className="flex items-center">
            <div
              className="border border-red-500 text-red-500 flex items-center w-max gap-1 px-1.5 py-0.5 rounded-md cursor-pointer"
              onClick={handleRemoveToken}
            >
              <Trash2 className="w-3 h-3" />
              <span className="text-xs font-medium">{t("Remove")}</span>
            </div>
          </div>
        ),
      };
    });
  }, [debouncedSearchText, myToken, showCopy]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClickAddAccessToken = () => {};

  const handleRemoveToken = () => {};

  return (
    <div className="py-4 max-w-5xl">
      <div className="px-4 sm:px-0">
        <h3 className="text-lg font-semibold text-gray-900">{t("Developer")}</h3>
        <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">
          {t("View and create access tokens for your workspace")}
        </p>
      </div>
      <div className="py-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center">
            <div className="relative w-full mr-2">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
              <input
                type="text"
                placeholder={t("Find access token")}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-600 text-sm font-medium placeholder-gray-500"
                onChange={(e) => {
                  const newVal = e.target.value || "";
                  setSearchText(newVal);
                }}
              />
            </div>
          </div>
          <div>
            <button
              onClick={handleClickAddAccessToken}
              className="flex items-center gap-2 text-white bg-blue-600 rounded-lg px-3 py-2 font-medium cursor-pointer"
            >
              <Key className="w-4 h-4" />
              {t("Add Access Token")}
            </button>
          </div>
        </div>
        <GridTable headers={tokenHeaders} rows={filterData} pageSize={10} />
      </div>
    </div>
  );
};

export default DeveloperSetting;
